/**
 * IndexedDB storage (Dexie). Local-first, on-device only. One table per
 * collection keyed by `id`, plus a `meta` singleton for profile/version.
 *
 * `replaceAll` implements the FULL-REPLACE import semantics (§0.9): wipe every
 * table inside one transaction, then bulk-insert the imported store.
 */
import Dexie, { type Table } from "dexie";
import type {
  CanonicalStore,
  Car,
  FuelLog,
  Spending,
  Tyre,
  TyreSet,
  Trip,
  MaintenanceReminder,
  PreferredService,
  Profile,
} from "../model.ts";
import { CURRENT_VERSION, emptyStore } from "../model.ts";

interface MetaRow {
  key: "app";
  schemaVersion: number;
  profile: Profile;
}

/** Binary attachment (photo/document), keyed by its globally-unique filename. */
interface AttachmentRow {
  filename: string;
  blob: Blob;
}

export class CarSpendDb extends Dexie {
  cars!: Table<Car, string>;
  fuelLogs!: Table<FuelLog, string>;
  spendings!: Table<Spending, string>;
  tyres!: Table<Tyre, string>;
  tyreSets!: Table<TyreSet, string>;
  trips!: Table<Trip, string>;
  maintenanceReminders!: Table<MaintenanceReminder, string>;
  services!: Table<PreferredService, string>;
  meta!: Table<MetaRow, string>;
  /** Photo/document binaries, keyed by filename. Managed separately from the
   *  data tables (not wiped by replaceAll) — import writes them explicitly. */
  attachments!: Table<AttachmentRow, string>;

  constructor(name = "carspend") {
    super(name);
    this.version(1).stores({
      cars: "id",
      fuelLogs: "id, carId",
      spendings: "id, carId",
      tyres: "id, carId",
      tyreSets: "id, carId",
      trips: "id, carId",
      maintenanceReminders: "id, carId",
      services: "id",
      meta: "key",
    });
    this.version(2).stores({
      cars: "id",
      fuelLogs: "id, carId",
      spendings: "id, carId",
      tyres: "id, carId",
      tyreSets: "id, carId",
      trips: "id, carId",
      maintenanceReminders: "id, carId",
      services: "id",
      meta: "key",
      attachments: "filename",
    });
  }

  // ── Attachments (blobs) ──────────────────────────────────────────────
  async putAttachment(filename: string, blob: Blob): Promise<void> {
    await this.attachments.put({ filename, blob });
  }
  async getAttachment(filename: string): Promise<Blob | undefined> {
    return (await this.attachments.get(filename))?.blob;
  }
  async deleteAttachment(filename: string): Promise<void> {
    await this.attachments.delete(filename);
  }
  async clearAttachments(): Promise<void> {
    await this.attachments.clear();
  }
  async allAttachmentFilenames(): Promise<string[]> {
    return (await this.attachments.toArray()).map((a) => a.filename);
  }

  private dataTables(): Table<{ id: string }, string>[] {
    return [
      this.cars as unknown as Table<{ id: string }, string>,
      this.fuelLogs as unknown as Table<{ id: string }, string>,
      this.spendings as unknown as Table<{ id: string }, string>,
      this.tyres as unknown as Table<{ id: string }, string>,
      this.tyreSets as unknown as Table<{ id: string }, string>,
      this.trips as unknown as Table<{ id: string }, string>,
      this.maintenanceReminders as unknown as Table<{ id: string }, string>,
      this.services as unknown as Table<{ id: string }, string>,
    ];
  }

  /** FULL REPLACE: clear everything, then write the imported store atomically. */
  async replaceAll(store: CanonicalStore): Promise<void> {
    await this.transaction(
      "rw",
      [...this.dataTables(), this.meta] as Table[],
      async () => {
        await Promise.all(this.dataTables().map((t) => t.clear()));
        await this.meta.clear();
        await this.cars.bulkAdd(store.cars);
        await this.fuelLogs.bulkAdd(store.fuelLogs);
        await this.spendings.bulkAdd(store.spendings);
        await this.tyres.bulkAdd(store.tyres);
        await this.tyreSets.bulkAdd(store.tyreSets);
        await this.trips.bulkAdd(store.trips);
        await this.maintenanceReminders.bulkAdd(store.maintenanceReminders);
        await this.services.bulkAdd(store.services);
        await this.meta.put({ key: "app", schemaVersion: store.schemaVersion, profile: store.profile });
      },
    );
  }

  /** Read the whole store back out (skip report is import-time only, so empty here). */
  async loadAll(): Promise<CanonicalStore> {
    const meta = await this.meta.get("app");
    const profile: Profile = meta?.profile ?? { displayCurrency: "USD", unitSystem: "metric" };
    const [cars, fuelLogs, spendings, tyres, tyreSets, trips, maintenanceReminders, services] =
      await Promise.all([
        this.cars.toArray(),
        this.fuelLogs.toArray(),
        this.spendings.toArray(),
        this.tyres.toArray(),
        this.tyreSets.toArray(),
        this.trips.toArray(),
        this.maintenanceReminders.toArray(),
        this.services.toArray(),
      ]);
    return {
      schemaVersion: meta?.schemaVersion ?? CURRENT_VERSION,
      profile,
      cars,
      fuelLogs,
      spendings,
      tyres,
      tyreSets,
      trips,
      maintenanceReminders,
      services,
      skipped: [],
    };
  }

  async isEmpty(): Promise<boolean> {
    return (await this.cars.count()) === 0;
  }
}

export function freshStore(displayCurrency = "USD"): CanonicalStore {
  return emptyStore({ displayCurrency, unitSystem: "metric" });
}
